<script>
  import { onMount } from 'svelte';

  import { Button, DataTable, InlineNotification, NotificationActionButton, Toolbar, ToolbarContent, Link } from 'carbon-components-svelte';
  import identity from 'lodash/identity';
  import { useNavigate } from 'svelte-navigator';

  import Reload from 'carbon-icons-svelte/lib/WatsonHealthRotate_18020';

  import AddDataFeedModal from './AddDataFeedModal.svelte';
  import DataFeedInventoryReloadModal from './DataFeedInventoryReloadModal.svelte';
  import LoadingSpinner from '../../components/LoadingSpinner.svelte';
  import PaginationWithRouting from '../../components/PaginationWithRouting.svelte';
  import StatusIcon from '../../components/StatusIcon.svelte';
  import ToolbarSearchWithRouting from '../../components/ToolbarSearchWithRouting.svelte';

  import { getDataFeedInventorySummary, refreshAndGetDataFeedInventorySummary } from '../../services';
  import { asBooleanStatusColumn, rowContainsText } from '../../utils';
  import { clickOrEnter } from '../../utils/clickOrEnter';
  import { getDataFeedEntitlementsReportUrl } from '../reports/utils/entitlements-report-routing';

  const navigate = useNavigate();
  const tableHeaders = [
    { key: 'name', value: 'Feed Name' },
    { key: 'awsLakeAvailable', value: 'AWS Lake', ...asBooleanStatusColumn },
    { key: 'awsArbitratedPcapsAvailable', value: 'AWS Arbitrated PCAPs', ...asBooleanStatusColumn },
    { key: 'awsRawFeedsAvailable', value: 'AWS Raw Feeds', ...asBooleanStatusColumn },
  ];

  let pageData = { loading: true };
  let query = { currentPage: 1, pageSize: 50, searchText: '' };
  let shouldUpdate = false;

  let modals = {
    addFeed: { open: false },
    reload: { open: false },
  };

  const permissionTypeMap = {
    awsLakeAvailable: 'data-feeds-lake',
    awsArbitratedPcapsAvailable: 'data-feeds-aws-arbitrated-pcaps',
    awsRawFeedsAvailable: 'data-feeds-aws-raw',
  };

  onMount(fetchInventory);

  async function fetchInventory() {
    pageData = { ...pageData, loading: true };

    try {
      const dataFeeds = await getDataFeedInventorySummary();
      pageData = { dataFeeds, loading: false };
    } catch (error) {
      console.error('[DataFeedInventory] Failed to load feed inventory!', error);
      pageData = { loading: false, error };
    }
  }

  async function refreshInventory() {
    pageData = { ...pageData, loading: true };
    shouldUpdate = false;

    try {
      const refreshedDataFeeds = await refreshAndGetDataFeedInventorySummary();
      pageData = { ...pageData, dataFeeds: refreshedDataFeeds, loading: false };
    } catch (error) {
      console.error('[DataFeedInventory] Failed to refresh feed inventory!', error);
      pageData = { loading: false, error };
      // eslint-disable-next-line svelte/infinite-reactive-loop
      shouldUpdate = true;
    }
  }

  async function updatePageData(modification) {
    const { action, data } = modification;

    switch (action) {
      case 'add-feed':
        if (data?.id) {
          await fetchInventory();
          query = { ...query, currentPage: 1, searchText: data.id };
        }
        break;
      case 'reload':
        shouldUpdate = true;
        break;
      default:
        console.warn(`[DataFeedInventory] Unknown action "${action}" on modal close. Define action's expected behavior. Data:`, data);
    }
  }

  function onSearchTextChange(e) {
    query = { ...query, currentPage: 1, searchText: e?.detail ?? '' };
  }

  function viewDataFeedDetails(event, dataFeedId) {
    event.preventDefault();
    navigate(`/data-feed/${dataFeedId}`);
  }

  function viewDataFeedSubscribers(dataFeedId, permissionType) {
    navigate(getDataFeedEntitlementsReportUrl(dataFeedId, permissionType));
  }

  function openModal(name, forwardData) {
    modals = {
      ...modals,
      [name]: { open: true, data: forwardData },
    };
  }

  function closeModal(name, modification = null) {
    if (modification) {
      updatePageData(modification);
    }

    modals = {
      ...modals,
      [name]: { open: false },
    };
  }

  $: filteredFeeds = pageData.dataFeeds?.filter(query.searchText ? (feed) => rowContainsText(feed, query.searchText) : identity);

  $: currentNotification = (() => {
    if (pageData.error) {
      return {
        kind: 'error',
        title: 'Error:',
        subtitle: 'Failed to load feed inventory! Please try again later.',
      };
    }
    if (pageData.dataFeeds?.length === 0) {
      return {
        kind: 'info',
        title: 'Info:',
        subtitle: 'No data feeds available. Data inventory sync required.',
        action: {
          text: 'View tasks',
          onClick: () => navigate('/tasks'),
        },
      };
    }

    return null;
  })();

  $: activeNotification = (() => {
    if (shouldUpdate) {
      return {
        kind: 'warning',
        title: 'Inventory Reload Possible!',
        subtitle: 'An inventory reload is possible. Click "Reload" for any new feed(s) to appear below.',
        action: {
          text: 'Reload',
          // eslint-disable-next-line svelte/infinite-reactive-loop
          onClick: () => refreshInventory(),
        },
      };
    }

    return null;
  })();
</script>

{#if currentNotification}
  <InlineNotification hideCloseButton lowContrast {...currentNotification}>
    <div slot="actions">
      {#if currentNotification.action}
        <NotificationActionButton on:click={currentNotification.action?.onClick}>
          {currentNotification.action?.text}
        </NotificationActionButton>
      {/if}
    </div>
  </InlineNotification>
{:else}
  {#if activeNotification}
    <InlineNotification hideCloseButton lowContrast {...activeNotification}>
      <div slot="actions">
        {#if activeNotification.action}
          <NotificationActionButton on:click={activeNotification.action?.onClick}>
            {activeNotification.action?.text}
          </NotificationActionButton>
        {/if}
      </div>
    </InlineNotification>
  {/if}
  <LoadingSpinner loading={pageData.loading}>
    <DataTable headers={tableHeaders} page={query.currentPage} pageSize={query.pageSize} rows={filteredFeeds ?? []} sortable>
      <span slot="cell" let:cell let:row>
        {#if cell.key === 'name'}
          <Link href={`/data-feed/${row.id}`} on:click={(event) => viewDataFeedDetails(event, row.id)}>{row.name}</Link>
        {:else if ['awsLakeAvailable', 'awsArbitratedPcapsAvailable', 'awsRawFeedsAvailable'].includes(cell.key)}
          <span
            role="button"
            tabindex="0"
            use:clickOrEnter={() => {
              if (!cell.value) return;
              viewDataFeedSubscribers(row.id, permissionTypeMap[cell.key]);
            }}><StatusIcon valid={cell.value} /></span
          >
        {:else if cell.display}
          {cell.display(cell.value, row)}
        {:else}
          {cell.value ?? '—'}
        {/if}
      </span>
      <Toolbar>
        <ToolbarContent>
          <ToolbarSearchWithRouting
            placeholder="Search feeds..."
            persistent
            bind:searchText={query.searchText}
            on:toolbarSearchValueChange={onSearchTextChange}
          />
          <div class="reload">
            <Button
              tooltipPosition="top"
              tooltipAlignment="center"
              iconDescription="Reload inventory"
              icon={Reload}
              kind="tertiary"
              disabled={pageData.loading || shouldUpdate}
              on:click={() => openModal('reload')}
            />
          </div>
          <Button on:click={() => openModal('addFeed')}>Add feed</Button>
        </ToolbarContent>
      </Toolbar>
    </DataTable>
    <PaginationWithRouting
      pageSizes={[10, 25, 50]}
      totalItems={filteredFeeds?.length ?? 0}
      bind:page={query.currentPage}
      bind:pageSize={query.pageSize}
    />
  </LoadingSpinner>
{/if}

{#if modals.addFeed.open}
  <AddDataFeedModal open on:close={({ detail: data }) => closeModal('addFeed', { action: 'add-feed', data })} />
{/if}

{#if modals.reload.open}
  <DataFeedInventoryReloadModal
    open
    on:close={() => closeModal('reload')}
    on:submit={() => closeModal('reload', { action: 'reload' })}
    disabled={pageData.loading || shouldUpdate}
  />
{/if}

<style>
  .reload :global(button > svg) {
    margin-left: unset !important;
  }
</style>
